import GetApp from '@mui/icons-material/GetApp'
import {
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	Tab,
	Tabs,
	Typography,
} from '@mui/material'
import { range } from 'lodash'
import * as React from 'react'
import styled from 'styled-components'
import { FlexBoxRight } from '..'
import { DayChartLine, DayLogById, Log } from '../../types'
import type { CsvExportInterval } from '../../utils'
import {
	downloadRangeCSV,
	downloadRangeFullCSV,
	isAllLogEnableHouse,
	isSoilHmdEnabledHouse,
	isProtoHouse,
} from '../../utils'
import { specialConfig } from '../../hooks/useLocalConfig'
import DateRangeDownloadDialog from '../DateRangeDownloadDialog'
import DayChart from '../DayChart'
import LogDateSelector from '../LogDateSelector'

export type Props = {
	dayLogs: DayLogById
	day: string
	isToday: boolean
	houseId: number
	handleSubmit: (day: string) => void
	handleSubmitToday: () => void
}

/*

・温度：赤
・湿度：青
・飽差：紫
・露点：水色
・CO2：黒
・土中温度A：赤白のゼブラ(無理な場合は赤系)
・土中温度B：赤黒のゼブラ(無理な場合は赤系)
・日射：オレンジ
*/

export const lines: { [key: string]: DayChartLine } = {
	tmp: {
		dataKey: 'tmp',
		stroke: 'red',
	},
	hmd: {
		dataKey: 'hmd',
		stroke: 'blue',
	},
	vpd: {
		dataKey: 'vpd',
		stroke: 'purple',
	},
	co2: {
		dataKey: 'co2',
		stroke: 'black',
	},
	lux: {
		dataKey: 'lux',
		stroke: 'orange',
	},
	dew: {
		dataKey: 'dew',
		stroke: '#35f2ff',
	},
	tmp2: {
		dataKey: 'tmp2',
		stroke: '#ff6b6b',
	},
	tmp3: {
		dataKey: 'tmp3',
		stroke: '#8b0000',
	},
	tmpAvg: {
		dataKey: 'tmpAvg' as keyof Log,
		stroke: '#ff1493',
	},
	soilHmd: {
		dataKey: 'soilHmd',
		stroke: '#00897b',
	},
}

const Style = styled.div`
	.MuiTabs-flexContainer > button {
		min-width: 40px;
	}
`
const StyledTab = styled(Tab)<{ color: string }>`
	span.MuiTab-wrapper {
		border-bottom: solid ${(p) => p.color};
	}
`

type LogWithAvg = Log & { tmpAvg?: number }
type LineKey =
	| 'tmp'
	| 'hmd'
	| 'vpd'
	| 'dew'
	| 'lux'
	| 'co2'
	| 'tmp2'
	| 'tmp3'
	| 'tmpAvg'
	| 'soilHmd'

const defaultVisibility: Record<LineKey, boolean> = {
	tmp: true,
	hmd: true,
	vpd: true,
	dew: true,
	lux: true,
	co2: true,
	tmp2: true,
	tmp3: true,
	tmpAvg: false,
	soilHmd: true,
}

function LogGraph(props: Props) {
	const [value, setValue] = React.useState<
		| 'all'
		| 'tmp'
		| 'hmd'
		| 'vpd'
		| 'dew'
		| 'co2'
		| 'lux'
		| 'tmp2'
		| 'tmp3'
		| 'tmpAvg'
		| 'soilHmd'
	>('all')
	const [rangeDialogOpen, setRangeDialogOpen] = React.useState(false)
	const [isDownloading, setIsDownloading] = React.useState(false)
	const { day } = props
	const showProtoFeatures = isAllLogEnableHouse(props.houseId)
	const showProtoHouse = isProtoHouse(props.houseId)
	const showSoilHmd = isSoilHmdEnabledHouse(props.houseId)

	const [visibleLines, setVisibleLines] =
		React.useState<Record<LineKey, boolean>>(defaultVisibility)

	const toggleLine = (key: LineKey) => {
		setVisibleLines((prev) => ({ ...prev, [key]: !prev[key] }))
	}

	const compositeLineItems: { key: LineKey; label: string; color: string }[] =
		React.useMemo(() => {
			const base: { key: LineKey; label: string; color: string }[] = [
				{ key: 'tmp', label: '温度', color: lines.tmp.stroke },
				{ key: 'hmd', label: '湿度', color: lines.hmd.stroke },
				{ key: 'vpd', label: '飽差', color: lines.vpd.stroke },
				{ key: 'dew', label: '露点', color: lines.dew.stroke },
				{ key: 'lux', label: '日射', color: lines.lux.stroke },
				{ key: 'co2', label: 'CO2', color: lines.co2.stroke },
			]
			if (showProtoFeatures) {
				base.push(
					{
						key: 'tmp2',
						label: specialConfig[props.houseId]?.tmp2 || '温度2',
						color: lines.tmp2.stroke,
					},
					{
						key: 'tmp3',
						label: specialConfig[props.houseId]?.tmp3 || '温度3',
						color: lines.tmp3.stroke,
					},
				)
			}
			if (showProtoHouse) {
				base.push({
					key: 'tmpAvg',
					label: '土中平均',
					color: lines.tmpAvg.stroke,
				})
			}
			if (showSoilHmd) {
				base.push({
					key: 'soilHmd',
					label: '土中湿度',
					color: lines.soilHmd.stroke,
				})
			}
			return base
		}, [showProtoFeatures, showProtoHouse, showSoilHmd, props.houseId])

	const filteredCompositeLines = React.useMemo(() => {
		const allLines = [
			lines.tmp,
			lines.hmd,
			lines.vpd,
			lines.dew,
			lines.lux,
			lines.co2,
			...(showProtoFeatures ? [lines.tmp2, lines.tmp3] : []),
			...(showProtoHouse ? [lines.tmpAvg] : []),
			...(showSoilHmd ? [lines.soilHmd] : []),
		]
		return allLines.filter((line) => visibleLines[line.dataKey as LineKey])
	}, [visibleLines, showProtoFeatures, showProtoHouse, showSoilHmd])

	const logsWithAvg = React.useMemo((): LogWithAvg[] => {
		return props.dayLogs.logs.map((log) => {
			console.log(log.tmp2, log.tmp3)

			const { tmp2, tmp3 } = log
			const hasValidTemps =
				tmp2 !== null && tmp3 !== null && !isNaN(tmp2) && !isNaN(tmp3)
			const tmpAvg = hasValidTemps ? (tmp2 + tmp3) / 2 : undefined

			return { ...log, tmpAvg }
		})
	}, [props.dayLogs.logs])

	const handleRangeDownload = async (
		startDate: string,
		endDate: string,
		fullColumns: boolean,
		exportInterval: CsvExportInterval,
	) => {
		setIsDownloading(true)
		try {
			if (fullColumns) {
				await downloadRangeFullCSV(
					props.houseId,
					String(props.houseId),
					startDate,
					endDate,
					exportInterval,
				)
			} else {
				await downloadRangeCSV(
					props.houseId,
					String(props.houseId),
					startDate,
					endDate,
					exportInterval,
				)
			}
		} finally {
			setIsDownloading(false)
		}
	}

	return (
		<Style>
			<Typography variant="h6">{props.day}</Typography>

			<FlexBoxRight>
				<LogDateSelector
					handleSubmit={props.handleSubmit}
					handleSubmitToday={props.handleSubmitToday}
					isToday={props.isToday}
					day={props.day.replace(/\//g, '-')}
				/>
				<Button
					variant="contained"
					color="primary"
					size="small"
					startIcon={<GetApp />}
					onClick={() => setRangeDialogOpen(true)}
					disabled={isDownloading}
					sx={{ whiteSpace: 'nowrap' }}
				>
					CSV
				</Button>
				{isDownloading && <CircularProgress size={20} />}
			</FlexBoxRight>

			<DateRangeDownloadDialog
				open={rangeDialogOpen}
				onClose={() => setRangeDialogOpen(false)}
				onDownload={handleRangeDownload}
				showFullOption={showProtoFeatures}
				showIntervalOption={showProtoFeatures}
			/>

			<Tabs
				value={value}
				onChange={(e, v) => setValue(v)}
				aria-label="wrapped label tabs example"
				variant="scrollable"
				scrollButtons="auto"
				allowScrollButtonsMobile
			>
				<Tab value="all" label="複合" wrapped />
				<StyledTab
					color={lines['tmp'].stroke}
					value="tmp"
					label="温度"
					wrapped
				/>
				<StyledTab
					color={lines['hmd'].stroke}
					value="hmd"
					label="湿度"
					wrapped
				/>
				<StyledTab
					color={lines['vpd'].stroke}
					value="vpd"
					label="飽差"
					wrapped
				/>
				<StyledTab
					color={lines['dew'].stroke}
					value="dew"
					label="露点"
					wrapped
				/>
				<StyledTab
					color={lines['lux'].stroke}
					value="lux"
					label="日射"
					wrapped
				/>
				<StyledTab
					color={lines['co2'].stroke}
					value="co2"
					label="CO2"
					wrapped
				/>
				{showProtoFeatures && (
					<StyledTab
						color={lines['tmp2'].stroke}
						value="tmp2"
						label={specialConfig[props.houseId]?.tmp2 || '温度2'}
						wrapped
					/>
				)}
				{showProtoFeatures && (
					<StyledTab
						color={lines['tmp3'].stroke}
						value="tmp3"
						label={specialConfig[props.houseId]?.tmp3 || '温度3'}
						wrapped
					/>
				)}
				{isProtoHouse(props.houseId) && (
					<StyledTab
						color={lines['tmpAvg'].stroke}
						value="tmpAvg"
						label="土中平均"
						wrapped
					/>
				)}
				{showSoilHmd && (
					<StyledTab
						color={lines['soilHmd'].stroke}
						value="soilHmd"
						label="土中湿度"
						wrapped
					/>
				)}
			</Tabs>

			<div style={{ margin: '0' }}>
				{value === 'all' && (
					<div>
						<DayChart
							houseId={props.houseId}
							day={day}
							logs={showProtoFeatures ? logsWithAvg : props.dayLogs.logs}
							yTicks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
							lines={filteredCompositeLines}
						/>
						<div
							style={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '0 8px',
								padding: '4px 8px',
							}}
						>
							{compositeLineItems.map((item) => (
								<FormControlLabel
									key={item.key}
									control={
										<Checkbox
											checked={visibleLines[item.key]}
											onChange={() => toggleLine(item.key)}
											size="small"
											sx={{
												color: item.color,
												'&.Mui-checked': { color: item.color },
												padding: '2px',
											}}
										/>
									}
									label={item.label}
									sx={{
										marginRight: 0,
										'& .MuiFormControlLabel-label': { fontSize: '0.8rem' },
									}}
								/>
							))}
						</div>
					</div>
				)}

				{value === 'tmp' && (
					<div>
						<DayChart
							houseId={props.houseId}
							day={day}
							logs={props.dayLogs.logs}
							yTicks={[0, 10, 20, 30, 40]}
							lines={[lines.tmp]}
						/>
					</div>
				)}

				{value === 'hmd' && (
					<div>
						<DayChart
							houseId={props.houseId}
							day={day}
							logs={props.dayLogs.logs}
							yTicks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
							lines={[lines.hmd]}
						/>
					</div>
				)}

				{value === 'vpd' && (
					<div>
						<DayChart
							houseId={props.houseId}
							day={day}
							logs={props.dayLogs.logs}
							yTicks={[0, 10, 20, 30, 40]}
							lines={[lines.vpd]}
						/>
					</div>
				)}

				{value === 'dew' && (
					<div>
						<DayChart
							houseId={props.houseId}
							day={day}
							logs={props.dayLogs.logs}
							yTicks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
							lines={[lines.dew]}
						/>
					</div>
				)}

				{value === 'lux' && (
					<div>
						<DayChart
							houseId={props.houseId}
							day={day}
							logs={props.dayLogs.logs}
							yTicks={range(0, 1500, 150)}
							lines={[lines.lux]}
						/>
					</div>
				)}

				{value === 'co2' && (
					<div>
						<DayChart
							houseId={props.houseId}
							day={day}
							logs={props.dayLogs.logs}
							yTicks={range(0, 1500, 150)}
							lines={[lines.co2]}
						/>
					</div>
				)}

				{showProtoFeatures && value === 'tmp2' && (
					<div>
						<DayChart
							houseId={props.houseId}
							day={day}
							logs={logsWithAvg}
							yTicks={[0, 10, 20, 30, 40]}
							lines={[lines.tmp2]}
						/>
					</div>
				)}

				{showProtoFeatures && value === 'tmp3' && (
					<div>
						<DayChart
							houseId={props.houseId}
							day={day}
							logs={logsWithAvg}
							yTicks={[0, 10, 20, 30, 40]}
							lines={[lines.tmp3]}
						/>
					</div>
				)}

				{isProtoHouse(props.houseId) && value === 'tmpAvg' && (
					<div>
						<DayChart
							houseId={props.houseId}
							day={day}
							logs={logsWithAvg}
							yTicks={[0, 10, 20, 30, 40]}
							lines={[lines.tmpAvg]}
						/>
					</div>
				)}

				{showSoilHmd && value === 'soilHmd' && (
					<div>
						<DayChart
							houseId={props.houseId}
							day={day}
							logs={props.dayLogs.logs}
							yTicks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
							lines={[lines.soilHmd]}
						/>
					</div>
				)}
			</div>
		</Style>
	)
}

export default LogGraph
